import React, { useState } from 'react';
import useAuthStore from '../../store/useAuthStore';
import useChatStore from '../../store/useChatStore';
import useVibeStore from '../../store/useVibeStore';

const CheckIcon = ({ className }) => <svg className={className} fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" /></svg>;
const SparkleIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09l2.846.813-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" /></svg>;
const CloseIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>;

const VibeResultCard = ({ data, chatId }) => {
    const { image, status } = data;
    const [accepted, setAccepted] = useState(false);
    const [open, setOpen] = useState(false);
    const { sendMessage } = useChatStore();
    const { user } = useAuthStore();

    const handleAccept = async () => {
        setAccepted(true);
        useVibeStore.getState().clearPendingAction();
        await sendMessage(chatId, "Accept changes", {
            isDirectCommand: true,
            command: 'vibe_accept'
        });
    };

    return (
        <div className="overflow-hidden rounded-2xl border border-[var(--border-subtle)]/50 bg-[var(--bg-secondary)] w-full max-w-sm sm:max-w-md shadow-md mt-2">
            {/* Header */}
            <div className="px-4 py-3 bg-[var(--bg-tertiary)]/80 border-b border-[var(--border-subtle)]/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <SparkleIcon className="w-4 h-4 text-purple-400" />
                    <span className="text-xs font-bold text-[var(--text-main)] uppercase tracking-widest">
                        Design Payload
                    </span>
                </div>
                <span className="text-[10px] font-semibold tracking-wider text-[var(--text-muted)] bg-[var(--bg-main)] px-2 py-0.5 rounded-full border border-[var(--border-subtle)]/50">
                    {status || "COMPLETED"}
                </span>
            </div>

            {/* Image Preview */}
            {image && (
                <>
                    <button
                        className="w-full h-48 sm:h-64 bg-black relative overflow-hidden group focus:outline-none"
                        onClick={() => setOpen(true)}
                    >
                        <div
                            className="absolute inset-0 bg-contain bg-no-repeat bg-center transition-transform duration-300 group-hover:scale-105"
                            style={{ backgroundImage: `url("${image}")` }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <span className="opacity-0 group-hover:opacity-100 bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 backdrop-blur-sm transition-opacity duration-300 shadow-xl border border-white/10">
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" /></svg>
                                Enlarge View
                            </span>
                        </div>
                    </button>

                    {/* Fullscreen Dialog */}
                    {open && (
                        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-200">
                            <button
                                onClick={() => setOpen(false)}
                                className="absolute top-6 right-6 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-50 focus:outline-none"
                            >
                                <CloseIcon className="w-6 h-6" />
                            </button>
                            <img
                                src={image}
                                alt="Full Preview"
                                className="max-w-full max-h-[90vh] object-contain cursor-zoom-out"
                                onClick={() => setOpen(false)}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Actions */}
            <div className="p-4 bg-[var(--bg-secondary)] flex gap-2">
                <button
                    disabled={accepted}
                    onClick={handleAccept}
                    className={`flex-1 py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 font-bold transition-all text-sm ${accepted
                        ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                        : 'bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-[var(--bg-main)] shadow-lg shadow-[var(--accent-primary)]/20 active:scale-[0.98]'
                        }`}
                >
                    <CheckIcon className="w-5 h-5" />
                    {accepted ? "Action Confirmed" :
                        status === 'waiting_for_proceed' ? "Proceed" :
                            status === 'waiting_for_run_terminal' ? "Run Terminal" :
                                status === 'waiting_for_accept_terminal' ? "Accept Terminal" :
                                    "Accept Changes"
                    }
                </button>
                {(status === 'waiting_for_run_terminal' || status === 'waiting_for_accept_terminal') && !accepted && (
                    <button
                        onClick={async () => {
                            setAccepted(true);
                            useVibeStore.getState().clearPendingAction();
                            await sendMessage(chatId, "Reject terminal", {
                                isDirectCommand: true,
                                command: 'vibe_reject'
                            });
                        }}
                        className="py-2.5 px-4 rounded-xl flex items-center justify-center font-bold transition-all text-sm bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20"
                    >
                        Reject
                    </button>
                )}
            </div>
        </div>
    );
};

export default VibeResultCard;
